import React, { useEffect, useState } from "react";
import { ChevronDown, Eye, Filter, Search, User } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { getAllEpcQuotes } from "../../store/slices/epcSlice";
import { RootDispatch, RootState } from "../../store";
import QuotationDetailsModal from "./QuotationDetailsModal";
import TableSkeleton from "../AssetsComponent/TableSkeleton";
import PaginationControls from "../pagination/PaginationControls";
import SearchInput from "../Search/SearchInput";
import { formatToDDMMYYYY } from "../../utils/formatDateUtils";
import Button, { ButtonType } from "../Assets/Button";

type ActiveFilter =
  | "salesRep"
  | "panelBrand"
  | "panelType"
  | "plantSize"
  | "status"
  | "dateRange";

const EPCQuotations: React.FC = () => {
  const [selectedQuotation, setSelectedQuotation] = useState<any>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isNextLoading, setIsNextLoading] = useState(false);
  const [isPrevLoading, setIsPrevLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [panelBrand, setPanelBrand] = useState<string>("");
  const [plantSize, setPlantSize] = useState<string>("");
  const [panelType, setPanelType] = useState<string>("");
  const [status, setStatus] = useState<"" | "true" | "false">("");
  const [salesRepId, setSalesRepId] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const [appliedStartDate, setAppliedStartDate] = useState<string>("");
  const [appliedEndDate, setAppliedEndDate] = useState<string>("");

  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);

  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = useSelector(
    (state: RootState) => state.epc.totalPagesOfQuotes,
  );
  const salesRepList = useSelector(
    (state: RootState) => state.epc.salesReps || [],
  );
  const loading = useSelector((state: RootState) => state.epc.loading);
  const epcQuotes =
    useSelector((state: RootState) => state.epc.epcQuotes) || [];

  const dispatch: RootDispatch = useDispatch();

  // Centralized fetching function
  const fetchQuotes = async (page: number = 1) => {
    await dispatch(
      getAllEpcQuotes({
        search: searchTerm,
        page,
        panelBrand: panelBrand || undefined,
        plantSize: plantSize || undefined,
        panelType: panelType || undefined,
        isConfirmed: status === "" ? undefined : status === "true",
        salesRepId: salesRepId || undefined,
        startDate: appliedStartDate || undefined,
        endDate: appliedEndDate || undefined,
      }),
    );
  };

  // Fetch on first load + pagination
  useEffect(() => {
    fetchQuotes(currentPage);
  }, [
    dispatch,
    currentPage,
    searchTerm,
    panelBrand,
    plantSize,
    panelType,
    status,
    salesRepId,
    appliedStartDate,
    appliedEndDate,
  ]);

  const handleViewDetails = (quotation: any) => {
    setSelectedQuotation(quotation);
    setIsDetailsModalOpen(true);
  };

  const handleNextPage = async () => {
    if (currentPage >= totalPages) return;
    setIsNextLoading(true);
    const nextPage = currentPage + 1;
    await fetchQuotes(nextPage);
    setCurrentPage(nextPage);
    setIsNextLoading(false);
  };

  const handlePreviousPage = async () => {
    if (currentPage <= 1) return;
    setIsPrevLoading(true);
    const prevPage = currentPage - 1;
    await fetchQuotes(prevPage);
    setCurrentPage(prevPage);
    setIsPrevLoading(false);
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);

  const clearFilters = () => {
    setPanelBrand("");
    setPlantSize("");
    setPanelType("");
    setStatus("");
    setSalesRepId("");
    setStartDate("");
    setEndDate("");
    setAppliedStartDate("");
    setAppliedEndDate("");
    setCurrentPage(1);
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {/* Search Bar */}
      <div className="p-4 flex-shrink-0 mb-4">
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative w-96 shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <SearchInput
              placeholder="Search quotes by customer..."
              className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-md 
               focus:ring-1 focus:ring-navyBlue-primary focus:border-navyBlue-primary 
               outline-none transition-all text-sm text-gray-700 
               placeholder:text-gray-400 shadow-sm"
              onSearch={(value) => {
                setSearchTerm(value);
                setCurrentPage(1);
              }}
            />
          </div>

          {/* Add Filter Dropdown */}
          <div
            className="relative inline-flex items-center gap-2 border rounded-md px-3 py-2 bg-white
               focus-within:ring-1 focus-within:ring-blue-500 min-w-[150px]"
          >
            <Filter className="h-4 w-4 text-gray-500 flex-shrink-0" />

            <select
              onChange={(e) => {
                const filter = e.target.value as ActiveFilter;
                if (filter && !activeFilters.includes(filter)) {
                  setActiveFilters((prev) => [...prev, filter]);
                }
                e.target.value = "";
              }}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            >
              <option value="">Add Filter</option>

              {!activeFilters.includes("salesRep") && (
                <option value="salesRep">Sales Rep</option>
              )}
              {!activeFilters.includes("panelBrand") && (
                <option value="panelBrand">Panel Brand</option>
              )}
              {!activeFilters.includes("panelType") && (
                <option value="panelType">Panel Type</option>
              )}
              {!activeFilters.includes("plantSize") && (
                <option value="plantSize">Plant Size</option>
              )}
              {!activeFilters.includes("status") && (
                <option value="status">Status</option>
              )}
              {!activeFilters.includes("dateRange") && (
                <option value="dateRange">Date Range</option>
              )}
            </select>

            <span className="flex-1 ml-2 text-sm text-gray-600 pointer-events-none whitespace-nowrap">
              Add Filter
            </span>
            <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0 pointer-events-none" />
          </div>
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div className="px-4 pb-3">
          <div className="bg-gray-50 border rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold text-gray-700">
                Filters
              </span>
              <Button
                type={ButtonType.Cancel}
                onClick={() => {
                  setActiveFilters([]);
                  clearFilters();
                }}
                className="text-xs bg-white text-red-500"
              >
                Clear all
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {/* Sales Rep */}
              {activeFilters.includes("salesRep") && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600">
                    Sales Representative
                  </label>
                  <select
                    value={salesRepId}
                    onChange={(e) => {
                      setSalesRepId(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="px-3 py-2 border rounded-md text-sm bg-white"
                  >
                    <option value="">All Sales Reps</option>
                    {salesRepList.map((rep: any) => (
                      <option key={rep._id} value={rep._id}>
                        {rep.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Panel Brand */}
              {activeFilters.includes("panelBrand") && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600">
                    Panel Brand
                  </label>

                  <SearchInput
                    placeholder="Search panel brand (e.g. Adani, Waaree)"
                    className="px-3 py-2 border rounded-md text-sm bg-white
     focus:outline-none focus:ring-1 focus:ring-blue-500"
                    onSearch={(value) => {
                      setPanelBrand(value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              )}

              {/* Panel Type */}
              {activeFilters.includes("panelType") && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600">
                    Panel Type
                  </label>
                  <select
                    value={panelType}
                    onChange={(e) => {
                      setPanelType(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="px-3 py-2 border rounded-md text-sm bg-white"
                  >
                    <option value="">All Types</option>
                    <option value="Bifacial (Mono PERC)">Bifacial</option>
                    <option value="Topcon">Topcon</option>
                  </select>
                </div>
              )}

              {/* Plant Size */}
              {activeFilters.includes("plantSize") && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600">
                    Plant Size
                  </label>
                  <SearchInput
                    placeholder="Plant size (4 or 4.5)"
                    onSearch={(value) => {
                      setPlantSize(value);
                      setCurrentPage(1);
                    }}
                    className="px-3 py-2 border rounded-md text-sm bg-white"
                  />
                </div>
              )}

              {/* Status */}
              {activeFilters.includes("status") && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600">
                    Status
                  </label>
                  <select
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value as any);
                      setCurrentPage(1);
                    }}
                    className="px-3 py-2 border rounded-md text-sm bg-white"
                  >
                    <option value="">All</option>
                    <option value="true">Confirmed</option>
                    <option value="false">Quoted</option>
                  </select>
                </div>
              )}

              {/* Date Range */}
              {activeFilters.includes("dateRange") && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600">
                    Date Range
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="px-3 py-2 border rounded-md text-sm bg-white"
                    />
                    <span className="text-xs text-gray-400">to</span>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="px-3 py-2 border rounded-md text-sm bg-white"
                    />
                    <Button
                      type={ButtonType.Extra}
                      onClick={() => {
                        setAppliedStartDate(startDate);
                        setAppliedEndDate(endDate);
                        setCurrentPage(1);
                      }}
                      className="text-xs inline-flex items-center gap-1"
                    >
                      <Search className="w-3.5 h-3.5" />
                      Apply
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quotations Table */}
      <div className="flex-1 px-4 pb-4 overflow-hidden">
        <div className="bg-white rounded-xl border border-gray-200 h-full flex flex-col overflow-hidden">
          {/* Table Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-700 uppercase tracking-wide">
              <div className="col-span-3">Customer</div>
              <div className="col-span-2">System Details</div>
              <div className="col-span-2">Sales Rep</div>
              <div className="col-span-2">Total Amount</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1">Created At</div>
              <div className="col-span-1">Actions</div>
            </div>
          </div>

          {/* Table Body */}
          <div className="flex-1 overflow-y-auto">
            <div className="divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 8 }).map((_, index) => (
                  <TableSkeleton key={index} />
                ))
              ) : epcQuotes.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500 text-sm">
                  No quotations found
                </div>
              ) : (
                epcQuotes.map((quotation, index) => (
                  <div
                    key={index}
                    className="px-6 py-4 hover:bg-gray-50/70 transition-colors"
                  >
                    <div className="grid grid-cols-12 gap-4 items-center">
                      {/* Customer */}
                      <div className="col-span-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-slate-600 to-slate-700 rounded-lg flex items-center justify-center text-white font-medium text-sm">
                            {quotation?.customerName?.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900 text-sm truncate">
                              {quotation?.customerName}
                            </div>
                            <div className="text-xs text-gray-500 font-mono">
                              {quotation?.mobileNumber}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* System Details */}
                      <div className="col-span-2">
                        <div className="text-sm font-medium text-gray-900">
                          {quotation?.brand}
                        </div>
                        <div className="text-xs text-gray-500">
                          {quotation?.plantSize} • {quotation?.panelType}
                        </div>
                      </div>

                      {/* Sales Rep */}
                      <div className="col-span-2">
                        <div className="flex items-center text-gray-600">
                          <User className="w-3 h-3 mr-1.5 text-gray-400" />
                          <span className="text-sm truncate">
                            {quotation?.salesRepresentative?.name ||
                              quotation?.cseID?.username}
                          </span>
                        </div>
                      </div>

                      {/* Amount */}
                      <div className="col-span-2 font-semibold text-gray-900">
                        {formatCurrency(quotation?.totalCost)}
                      </div>

                      {/* Status */}
                      <div className="col-span-1">
                        <span
                          className={`inline-flex items-center px-1 py-1 rounded-full text-xs font-medium ${
                            quotation?.isConfirmed ? " text-green-700" : ""
                          }`}
                        >
                          {quotation?.isConfirmed ? "Confirmed" : "Quoted"}
                        </span>
                      </div>
                      {/* Created At */}
                      <div className="col-span-1">
                        <div className="text-sm text-gray-900">
                          {formatToDDMMYYYY(quotation?.createdAt)}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="col-span-1">
                        <button
                          onClick={() => handleViewDetails(quotation)}
                          className="inline-flex items-center gap-1 px-2 py-1.5 text-xs bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 border border-blue-200"
                        >
                          <Eye className="w-3 h-3" />
                          View
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Pagination */}
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <PaginationControls
              isNextFetching={isNextLoading}
              isPrevFetching={isPrevLoading}
              currentPage={currentPage}
              totalPages={totalPages}
              onNext={handleNextPage}
              onPrev={handlePreviousPage}
            />
          </div>
        </div>
      </div>

      {/* Details Modal */}
      <QuotationDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedQuotation(null);
        }}
        quotation={selectedQuotation}
      />
    </div>
  );
};

export default EPCQuotations;
